import enhancedPptxService from './enhancedPptxService';
import { extractSlidesJsonFromDocumentHtml } from '../utils/presentationEmbed';

/** Matches page break markup (the editor's `pageBreak` node and legacy inline-style breaks). */
const PAGE_BREAK_HINT_RE = /data-page-break|break-(?:after|before)\s*:\s*(?:page|always)/i;
const LEGACY_PAGE_BREAK_STYLE_RE = /(?:page-break-(?:after|before)\s*:\s*always|break-(?:after|before)\s*:\s*page)/i;

/**
 * True when a top-level element only marks a page break.
 * @param {Element} el - DOM element.
 * @returns {boolean} Whether the element is a page break.
 */
function isPageBreakElement(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === 'div' && el.hasAttribute('data-page-break')) {
    return el.getAttribute('data-page-break') !== 'false';
  }
  if (tag !== 'div' && tag !== 'p') return false;
  if (!LEGACY_PAGE_BREAK_STYLE_RE.test(el.getAttribute('style') || '')) return false;
  return !el.textContent.trim() && !el.querySelector('img, table, svg, iframe, video');
}

/**
 * Splits document HTML at top-level page breaks.
 * @param {string} htmlContent - Document HTML.
 * @returns {Element[][]} Top-level elements per page (page breaks removed; may contain empty pages).
 */
export function splitHtmlAtPageBreaks(htmlContent) {
  const doc = new DOMParser().parseFromString(htmlContent || '', 'text/html');
  const pages = [[]];
  Array.from(doc.body.children).forEach((el) => {
    if (isPageBreakElement(el)) {
      pages.push([]);
    } else {
      pages[pages.length - 1].push(el);
    }
  });
  return pages;
}

/**
 * Groups HTML into slides where every page break starts a new slide. Within one page the normal
 * grouping (`enhancedPptxService.convertSectionsToSlides`: new slide on h1 / h2, at most N blocks
 * per slide) still applies. Empty pages (consecutive breaks) produce no slide.
 *
 * @param {string} htmlContent - Document HTML that contains at least one page break.
 * @param {object} [options] - Grouping options (`maxSectionsPerSlide`).
 * @returns {{ title: string, content: string }[]} Slide definitions with HTML content.
 */
export function buildSlidesFromPagedHtml(htmlContent, options = {}) {
  const slides = [];
  splitHtmlAtPageBreaks(htmlContent).forEach((elements) => {
    const blocks = elements
      .map((el) => {
        const tag = el.tagName.toLowerCase();
        const rawText = el.textContent.trim();
        const isHeading = tag === 'h1' || tag === 'h2' || tag === 'h3';
        const hasMedia = ['ul', 'ol', 'table', 'img', 'blockquote'].includes(tag) || Boolean(el.querySelector('img'));
        if (!isHeading && !hasMedia && !rawText) return null;
        // Only `type` and `rawText` drive the grouping; `html` rides along for the slide content.
        return { type: isHeading ? tag : 'block', rawText, html: el.outerHTML };
      })
      .filter(Boolean);
    if (blocks.length === 0) return;
    enhancedPptxService.convertSectionsToSlides(blocks, options).forEach((slide) => {
      slides.push({
        title: slide.title || '',
        content: slide.content.map((block) => block.html).join(''),
      });
    });
  });
  return slides;
}

/**
 * Single entry point for exporting stored document HTML to a .pptx file (browser download).
 */
class PptxExportService {
  /**
   * Export document HTML to PowerPoint, preferring embedded slide JSON when present. Page breaks
   * in the document start a new slide.
   * @param {string} htmlContent - Stored `generated_documents.content`.
   * @param {string} fileName - Target file name (e.g. `deck.pptx`).
   * @param {string} [_projectName] - Reserved for parity with other export services.
   * @param {object} [options] - Options passed to {@link enhancedPptxService.exportSlidesToPptx}.
   * @returns {Promise<boolean>} True when export succeeded.
   */
  async exportToPptx(htmlContent, fileName, _projectName = '', options = {}) {
    const slides = extractSlidesJsonFromDocumentHtml(htmlContent);
    if (slides && Array.isArray(slides) && slides.length > 0) {
      return enhancedPptxService.exportSlidesToPptx(slides, fileName, options);
    }
    if (typeof htmlContent === 'string' && PAGE_BREAK_HINT_RE.test(htmlContent)) {
      let pagedSlides = null;
      try {
        pagedSlides = buildSlidesFromPagedHtml(htmlContent, options);
      } catch (error) {
        console.warn('Page break slide grouping failed; using default grouping.', error);
      }
      if (pagedSlides && pagedSlides.length > 0) {
        return enhancedPptxService.exportSlidesToPptx(pagedSlides, fileName, options);
      }
    }
    return enhancedPptxService.exportHtmlToPptx(htmlContent, fileName, options);
  }
}

const pptxExportService = new PptxExportService();
export default pptxExportService;
